import { useState, useEffect, useRef } from "react";
import {
  Autocomplete,
  Modal,
  Box,
  Button,
  IconButton,
  Typography,
  TextField,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  Grid,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Picker from "emoji-picker-react";
import { getUtcTimestamp } from "../utils/Utils";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

// Firebase
import { app } from "../../firebaseConfig";
import { getStorage, ref, deleteObject } from "firebase/storage";

// Components
import { useNotification } from "../utils/Notification";
import LoadingButton from "../utils/LoadingButton";
import ConfirmationModal from "../utils/ConfirmationModal";
import { trackEvent } from "../utils/Utils";

// Context
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";

// Supabase
import supabase from "../../supabaseClient";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: "80%", md: "60%" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  overflowY: "auto",
  p: 4,
};

export default function QualitasMessage({ open, onClose, file }) {
  const [type, setType] = useState("");
  const [policy, setPolicy] = useState("");
  const [issuedDate, setIssuedDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [policyOwnerName, setPolicyOwnerName] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filePath, setFilePath] = useState("");
  const [conditionsRef, setConditionsRef] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { t } = useTranslation();
  const [messageOn, setMessageOn] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [contactError, setContactError] = useState(false);

  // Firebase
  const storage = getStorage(app);

  // Variables Menu State
  const [contactVariables, setContactVariables] = useState([
    "first_name",
    "last_name",
    "nickname",
    "birthday",
    "countrycode_id",
    "phone",
  ]);
  const [profileVariables, setProfileVariables] = useState([
    "first_name",
    "last_name",
    "nickname",
    "countrycode_id",
    "phone",
  ]);

  const [fileVariables, setFileVariables] = useState([]);
  const koubiVariables = ["day", "month", "year", "date"];
  const [showVariableMenu, setShowVariableMenu] = useState(false);
  const [showContactVariables, setShowContactVariables] = useState(false);
  const [showProfileVariables, setShowProfileVariables] = useState(false);
  const [showKoubiVariables, setShowKoubiVariables] = useState(false);
  const [showFileVariables, setShowFileVariables] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const contentRef = useRef(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Context
  const { profileId, teamId } = useAuth();
  const { showNotification } = useNotification();

  useEffect(() => {
    const fetchContacts = async () => {
      let allContacts = [];
      let offset = 0;
      const pageSize = 1000;

      while (true) {
        const { data, error } = await supabase
          .from("contacts")
          .select("id, first_name, last_name")
          .order("first_name", { ascending: true })
          .range(offset, offset + pageSize - 1);

        if (error) {
          showNotification(t("qualitasMessage.errorFetchingContacts"), "error");
          break;
        }

        if (data && data.length > 0) {
          allContacts = [...allContacts, ...data];
        }

        if (!data || data.length < pageSize) {
          break;
        }

        offset += pageSize;
      }

      setContacts(allContacts);
    };

    const fetchTemplates = async () => {
      const { data, error } = await supabase
        .from("templates")
        .select("id, name, content");

      if (error) {
        showNotification(t("qualitasMessage.errorFetchingTemplates"), "error");
      } else {
        setTemplates(data);
      }
    };

    const fetchContactVariables = async () => {
      const { data, error } = await supabase.from("variables").select("key");
      if (error) {
        showNotification(
          t("qualitasMessage.errorFetchingContactVariables"),
          "error"
        );
      } else {
        const variableKeys = data ? data.map((item) => item.key) : [];
        const allVariableKeys = [
          ...contactVariables, // Default variables
          ...variableKeys, // Fetched variables
        ];
        setContactVariables(allVariableKeys);
      }
    };

    const fetchProfileVariables = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("variables");
      if (error) {
        showNotification(
          t("qualitasMessage.errorFetchingProfileVariables"),
          "error"
        );
      } else {
        const variableKeys =
          data && data[0] && data[0].variables
            ? Object.keys(data[0].variables)
            : [];
        const allVariableKeys = [
          ...profileVariables, // Default variables
          ...variableKeys, // Fetched variables
        ];
        setProfileVariables(allVariableKeys);
      }
    };

    fetchTemplates();
    fetchContacts();
    fetchContactVariables();
    fetchProfileVariables();
  }, []);

  useEffect(() => {
    if (file) {
      setMessageOn(getNextRoundedTime());
      setType(file.type);
      setPolicy(file.policy);
      setIssuedDate(file.issuedDate || null);
      setExpirationDate(file.expirationDate || null);
      setPolicyOwnerName(file.name || null);
      setFilePath(file.file);
      setConditionsRef(file.conditions || null);
      setFileVariables(
        file.type === t("qualitasMessage.policy")
          ? [
              "policy",
              "conditions_ref",
              "issued_date",
              "expiration_date",
              "policy_owner_name",
            ]
          : ["policy", "expiration_date", "policy_owner_name"]
      );
    }
  }, [file]);

  const handleChange = (field, value) => {
    setShowEmojiPicker(false);
    switch (field) {
      case "contact":
        setSelectedContact(value);
        break;
      case "messageText":
        setMessageText(value);
        break;
      case "template":
        setSelectedTemplate(value);
        const template = templates.find((template) => template.id === value);
        setMessageText(template.content);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e) => {
    trackEvent("qualitasmessage-form_submit");
    e.preventDefault();

    if (!selectedContact || !messageOn) {
      if (!selectedContact) {
        setContactError(true);
      }
      showNotification(t("qualitasMessage.fillAllFields"), "warning");
      return;
    }

    setContactError(false);
    setIsLoading(true);
    const response = await fetch(
      `https://sendqualitasfiles-tiph5o4x3q-uc.a.run.app/?profileId=${profileId}&messageText=${encodeURIComponent(
        messageText
      )}&contactId=${selectedContact}&fileId=${
        file.id
      }&scheduledTimestamp=${getUtcTimestamp(messageOn)}`
    );
    const data = await response.json();
    setIsLoading(false);
    if (data.success) {
      showNotification(t("qualitasMessage.messageAndFilesSent"));
      resetValues();
      return;
    }

    const errorMessage =
      t(`qualitasMessage.responseErrors.${data.message}`) ||
      t("qualitasMessage.responseErrors.unknown_error");

    showNotification(errorMessage, "error");
  };

  const resetValues = () => {
    setSelectedContact(null);
    setMessageText("");
    setSelectedTemplate(null);
    setIsLoading(false);
    setShowDeleteConfirmation(false);
    setShowEmojiPicker(false);
    onClose();
  };

  // Handle variables menu:
  const handleContentChange = (e) => {
    const content = e.target.value;
    const cursorPosition = e.target.selectionStart;

    handleChange("messageText", content);

    if (content[cursorPosition - 1] === "@") {
      trackEvent("qualitasmessage-variables_menu_opened");
      const rect = contentRef.current.getBoundingClientRect();
      setMenuPosition({ x: rect.left, y: rect.bottom });
      setShowVariableMenu(true);
    } else {
      setShowVariableMenu(false);
      setShowContactVariables(false);
      setShowProfileVariables(false);
      setShowKoubiVariables(false);
      setShowFileVariables(false);
    }
  };
  const handleVariableSelect = (variable, type) => {
    trackEvent("qualitasmessage-variable_selected");
    const currentContent = messageText;
    const cursorPosition = contentRef.current.selectionStart;
    const newContent = `${currentContent.substring(
      0,
      cursorPosition - 1
    )}%${type}.${variable}%${currentContent.substring(cursorPosition)}`;
    handleChange("messageText", newContent);
    setShowContactVariables(false);
    setShowProfileVariables(false);
    setShowKoubiVariables(false);
    setShowVariableMenu(false);
    setShowFileVariables(false);
  };

  const handleDelete = () => {
    trackEvent("qualitasmessage-delete_clicked");
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirmation = async () => {
    trackEvent("qualitasmessage-delete_confirmed");
    try {
      setShowDeleteConfirmation(false);
      const storageRef = ref(storage, `files/${teamId}/${file.id}/${filePath}`);
      await deleteObject(storageRef);
      const { error } = await supabase.from("files").delete().eq("id", file.id);

      if (error) {
        throw error;
      }
      showNotification(t("qualitasMessage.fileDeleted"));
      resetValues();
    } catch (error) {
      showNotification(
        t("qualitasMessage.errorDeletingFile", { error: error.message }),
        "error"
      );
    }
  };

  const handleDeleteCancellation = () => {
    trackEvent("qualitasmessage-delete_cancelled");
    setShowDeleteConfirmation(false);
  };

  function getNextRoundedTime() {
    const now = new Date();
    const minutes = now.getMinutes();

    // Calculate the next multiple of 10
    const roundedMinutes = Math.ceil((minutes + 1) / 10) * 10;

    // If minutes go beyond 60, adjust the hour
    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0);
    } else {
      now.setMinutes(roundedMinutes);
    }

    now.setSeconds(0);
    now.setMilliseconds(0);

    return now;
  }

  const handleEmojiClick = (event) => {
    const cursorPosition = contentRef.current.selectionStart;
    const currentContent = messageText;
    const newContent =
      currentContent.substring(0, cursorPosition) +
      event.emoji +
      currentContent.substring(cursorPosition);
    handleChange("messageText", newContent);
  };

  return (
    <Box>
      <Modal open={open} onClose={resetValues}>
        <Box sx={style}>
          <form onSubmit={handleSubmit}>
            {/* Form Fields */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t("qualitasMessage.type")}
                  value={type}
                  disabled={true}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("qualitasMessage.policy")}
                  value={policy}
                  disabled={true}
                  fullWidth
                />
              </Grid>
              {issuedDate && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("qualitasMessage.issuedDate")}
                    value={issuedDate}
                    disabled={true}
                    fullWidth
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={issuedDate ? 6 : 12}>
                <TextField
                  label={
                    type === t("qualitasMessage.policy")
                      ? t("qualitasMessage.expirationDate")
                      : t("qualitasMessage.paymentLimit")
                  }
                  value={expirationDate}
                  disabled={true}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("qualitasMessage.policyOwnerName")}
                  value={policyOwnerName}
                  disabled={true}
                  fullWidth
                />
              </Grid>
              {/* Contact Selection */}
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <Autocomplete
                    id="contact-autocomplete"
                    options={contacts}
                    getOptionLabel={(option) =>
                      `${option.first_name} ${option.last_name}`
                    }
                    value={
                      contacts.find(
                        (contact) => contact.id === selectedContact
                      ) || null
                    }
                    onChange={(e, newValue) => {
                      handleChange("contact", newValue?.id);
                      setContactError(false);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("qualitasMessage.selectContact")}
                        placeholder={t("qualitasMessage.searchContact")}
                        error={contactError}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {`${option.first_name} ${option.last_name}`}
                      </li>
                    )}
                    noOptionsText={t("qualitasMessage.noContacts")}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="template-select-label">
                    {t("qualitasMessage.selectTemplate")}
                  </InputLabel>
                  <Select
                    labelId="template-select-label"
                    id="template-select"
                    value={selectedTemplate || ""}
                    label="Select Template"
                    onChange={(e) => handleChange("template", e.target.value)}
                  >
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">
                  {t("qualitasMessage.variablesTutorial")}
                  <a
                    href={t("qualitasMessage.variablesTutorialUrl")}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("qualitasMessage.tutorial")}
                  </a>
                  .
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    fullWidth
                    label={t("qualitasMessage.messageText")}
                    multiline
                    rows={4}
                    value={messageText}
                    inputRef={contentRef}
                    onChange={handleContentChange}
                    required
                    InputProps={{
                      // Padding from text inside TextField
                      sx: { paddingRight: "28px" },
                    }}
                  />
                  <IconButton
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    sx={{
                      position: "absolute",
                      top: -2,
                      right: -1,
                      color: "black",
                    }}
                  >
                    <span
                      role="img"
                      aria-label="emoji"
                      style={{ fontSize: "1rem" }}
                    >
                      😊
                    </span>
                  </IconButton>
                  {showEmojiPicker && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 40,
                        right: 0,
                        zIndex: 1000,
                        border: "1px solid #ddd",
                        backgroundColor: "white",
                      }}
                    >
                      <Picker onEmojiClick={handleEmojiClick} />
                    </Box>
                  )}
                </Box>
                <Menu
                  open={showVariableMenu}
                  onClose={() => setShowVariableMenu(false)}
                  anchorReference="anchorPosition"
                  anchorPosition={{
                    top: menuPosition.y,
                    left: menuPosition.x,
                  }}
                >
                  <MenuItem onClick={() => setShowContactVariables(true)}>
                    {t("qualitasMessage.contact")}
                    <Menu
                      open={showContactVariables}
                      onClose={() => setShowContactVariables(false)}
                      anchorReference="anchorPosition"
                      anchorPosition={{
                        top: menuPosition.y,
                        left: menuPosition.x,
                      }}
                    >
                      {contactVariables.map((variable) => (
                        <MenuItem
                          key={variable}
                          onClick={() =>
                            handleVariableSelect(variable, "contact")
                          }
                        >
                          {variable}
                        </MenuItem>
                      ))}
                    </Menu>
                  </MenuItem>
                  <MenuItem onClick={() => setShowFileVariables(true)}>
                    {t("qualitasMessage.file")}
                    <Menu
                      open={showFileVariables}
                      onClose={() => setShowFileVariables(false)}
                      anchorReference="anchorPosition"
                      anchorPosition={{
                        top: menuPosition.y,
                        left: menuPosition.x,
                      }}
                    >
                      {fileVariables.map((variable) => (
                        <MenuItem
                          key={variable}
                          onClick={() => handleVariableSelect(variable, "file")}
                        >
                          {variable}
                        </MenuItem>
                      ))}
                    </Menu>
                  </MenuItem>
                  <MenuItem onClick={() => setShowProfileVariables(true)}>
                    {t("qualitasMessage.profile")}
                    <Menu
                      open={showProfileVariables}
                      onClose={() => setShowProfileVariables(false)}
                      anchorReference="anchorPosition"
                      anchorPosition={{
                        top: menuPosition.y,
                        left: menuPosition.x,
                      }}
                    >
                      {profileVariables.map((variable) => (
                        <MenuItem
                          key={variable}
                          onClick={() =>
                            handleVariableSelect(variable, "profile")
                          }
                        >
                          {variable}
                        </MenuItem>
                      ))}
                    </Menu>
                  </MenuItem>
                  <MenuItem onClick={() => setShowKoubiVariables(true)}>
                    {t("qualitasMessage.koubi")}
                    <Menu
                      open={showKoubiVariables}
                      onClose={() => setShowKoubiVariables(false)}
                      anchorReference="anchorPosition"
                      anchorPosition={{
                        top: menuPosition.y,
                        left: menuPosition.x,
                      }}
                    >
                      {koubiVariables.map((variable) => (
                        <MenuItem
                          key={variable}
                          onClick={() =>
                            handleVariableSelect(variable, "koubi")
                          }
                        >
                          {variable}
                        </MenuItem>
                      ))}
                    </Menu>
                  </MenuItem>
                </Menu>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("qualitasMessage.filePath")}
                  value={filePath}
                  disabled={true}
                  fullWidth
                />
              </Grid>
              {conditionsRef && (
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TextField
                    label={t("qualitasMessage.conditionsRef")}
                    value={conditionsRef}
                    disabled={true}
                    fullWidth
                  />
                  {/* Information Tooltip Icon */}
                  <Tooltip
                    title={
                      <h2 style={{ fontSize: "13px" }}>
                        {t("qualitasMessage.labelConditions")}
                      </h2>
                    }
                    open={tooltipOpen}
                    onClose={() => setTooltipOpen(false)}
                    disableHoverListener
                  >
                    <IconButton
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() => setTooltipOpen(true)}
                    >
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label={t("qualitasMessage.messageOn")}
                    value={messageOn ? dayjs(messageOn) : null}
                    onChange={(newValue) => {
                      if (newValue && newValue.isValid()) {
                        setMessageOn(newValue.toISOString());
                      } else {
                        setMessageOn(newValue);
                      }
                    }}
                    inputFormat="YYYY-MM-DD, hh:mm A"
                    renderInput={(params) => (
                      <TextField {...params} fullWidth required />
                    )}
                    minutesStep={10}
                  />
                </LocalizationProvider>
              </Grid>
              <LoadingButton
                variant="contained"
                color="secondary"
                sx={{ mt: 2, ml: 2 }}
                type="submit"
                loading={isLoading}
                onClick={handleSubmit}
              >
                {type === t("qualitasMessage.policy")
                  ? t("qualitasMessage.sendPolicyAndConditions")
                  : t("qualitasMessage.sendReceipt")}
              </LoadingButton>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                sx={{ mt: 2, ml: 2 }}
              >
                {t("qualitasMessage.delete")}
              </Button>
            </Grid>
          </form>
          <ConfirmationModal
            open={showDeleteConfirmation}
            onClose={handleDeleteCancellation}
            onConfirm={handleDeleteConfirmation}
            title={t("qualitasMessage.confirmDelete")}
            message={t("qualitasMessage.confirmDeleteMessage")}
          />
        </Box>
      </Modal>
    </Box>
  );
}
